import React, { useState, useEffect } from 'react';

interface SessionData {
  id: string;
  startTime: string;
  endTime?: string;
  duration?: string;
  accomplishments: Array<{
    type: string;
    description: string;
  }>;
  patternsLearned: Array<{
    name: string;
    description: string;
    successRate: number;
  }>;
  nextSteps?: string[];
}

interface Pattern {
  name: string;
  description: string;
  successRate: number;
  usageCount: number;
  lastUsed: string;
}

export default function ClaudeProgress() {
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [currentSession, setCurrentSession] = useState<SessionData | null>(null);
  const [metrics, setMetrics] = useState({
    totalSessions: 0,
    totalAccomplishments: 0,
    averageSuccessRate: 0,
    topPatterns: [] as Pattern[]
  });

  useEffect(() => {
    loadProgressData();
    const interval = setInterval(loadProgressData, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const loadProgressData = async () => {
    try {
      // In a real implementation, this would fetch from the file system or API
      // For now, we'll use mock data to demonstrate the concept
      const mockSessions: SessionData[] = [
        {
          id: 'session-1',
          startTime: new Date(Date.now() - 86400000).toISOString(),
          endTime: new Date(Date.now() - 82800000).toISOString(),
          duration: '1h 0m',
          accomplishments: [
            { type: 'feat', description: 'feat: Added betting UI improvements' },
            { type: 'fix', description: 'fix: Resolved balance tracking issue' }
          ],
          patternsLearned: [
            { name: 'incremental-improvement', description: 'Small steps lead to progress', successRate: 0.9 }
          ],
          nextSteps: ['Test with real users', 'Add more markets']
        }
      ];

      const mockPatterns: Pattern[] = [
        {
          name: 'bug-fix-pattern',
          description: 'Identify, isolate, fix, test',
          successRate: 0.85,
          usageCount: 12,
          lastUsed: new Date().toISOString()
        },
        {
          name: 'feature-development',
          description: 'Design, implement, test, document',
          successRate: 0.92,
          usageCount: 8,
          lastUsed: new Date().toISOString()
        }
      ];

      setSessions(mockSessions);
      setPatterns(mockPatterns);

      // Calculate metrics
      const totalAccomplishments = mockSessions.reduce(
        (sum, s) => sum + s.accomplishments.length,
        0
      );

      const avgSuccess = mockPatterns.reduce(
        (sum, p) => sum + p.successRate,
        0
      ) / (mockPatterns.length || 1);

      setMetrics({
        totalSessions: mockSessions.length,
        totalAccomplishments,
        averageSuccessRate: avgSuccess,
        topPatterns: mockPatterns.sort((a, b) => b.successRate - a.successRate).slice(0, 3)
      });

    } catch (error) {
      console.error('Error loading progress data:', error);
    }
  };

  const getProgressColor = (rate: number) => {
    if (rate >= 0.9) return 'text-green-500';
    if (rate >= 0.7) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold mb-2">Claude Progress Tracker</h1>
        <p className="text-gray-400 mb-8">Continuous improvement through iteration</p>

        {/* Metrics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total Sessions</div>
            <div className="text-3xl font-bold text-blue-400">{metrics.totalSessions}</div>
          </div>

          <div className="bg-gray-800 rounded-xl p-6">
            <div className="text-sm text-gray-400 mb-1">Accomplishments</div>
            <div className="text-3xl font-bold text-green-400">{metrics.totalAccomplishments}</div>
          </div>

          <div className="bg-gray-800 rounded-xl p-6">
            <div className="text-sm text-gray-400 mb-1">Success Rate</div>
            <div className={`text-3xl font-bold ${getProgressColor(metrics.averageSuccessRate)}`}>
              {(metrics.averageSuccessRate * 100).toFixed(0)}%
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl p-6">
            <div className="text-sm text-gray-400 mb-1">Patterns Learned</div>
            <div className="text-3xl font-bold text-purple-400">{patterns.length}</div>
          </div>
        </div>

        {/* Pattern Recognition */}
        <div className="bg-gray-800 rounded-xl p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-purple-400">🧠 Learned Patterns</h2>
          <div className="space-y-4">
            {patterns.map(pattern => (
              <div key={pattern.name} className="bg-gray-900 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-bold text-lg">{pattern.description}</div>
                    <div className="text-sm text-gray-400">
                      Used {pattern.usageCount} times
                    </div>
                  </div>
                  <div className={`text-2xl font-bold ${getProgressColor(pattern.successRate)}`}>
                    {(pattern.successRate * 100).toFixed(0)}%
                  </div>
                </div>

                {/* Success rate bar */}
                <div className="w-full bg-gray-700 rounded-full h-2 mt-3">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-blue-500"
                    style={{ width: `${pattern.successRate * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Session History */}
        <div className="bg-gray-800 rounded-xl p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-blue-400">📊 Recent Sessions</h2>
          <div className="space-y-4">
            {sessions.slice(0, 5).map(session => (
              <div key={session.id} className="bg-gray-900 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-bold">{session.id}</div>
                    <div className="text-sm text-gray-400">
                      Duration: {session.duration || 'In progress'}
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">
                    {new Date(session.startTime).toLocaleDateString()}
                  </div>
                </div>

                {session.accomplishments.length > 0 && (
                  <div className="mb-3">
                    <div className="text-sm font-bold text-green-400 mb-1">Accomplishments:</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      {session.accomplishments.map((acc, i) => (
                        <li key={i}>• {acc.description}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {session.nextSteps && session.nextSteps.length > 0 && (
                  <div>
                    <div className="text-sm font-bold text-yellow-400 mb-1">Next Steps:</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      {session.nextSteps.slice(0, 3).map((step, i) => (
                        <li key={i}>→ {step}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Automation Status */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-2xl font-bold mb-4 text-green-400">🤖 Automation Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-900 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Session Hooks</span>
                <span className="text-green-400">✓ Active</span>
              </div>
              <div className="text-xs text-gray-500">
                Auto-loading context and saving progress
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Pattern Learning</span>
                <span className="text-green-400">✓ Active</span>
              </div>
              <div className="text-xs text-gray-500">
                Identifying successful approaches
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Auto-Testing</span>
                <span className="text-yellow-400">⚡ Ready</span>
              </div>
              <div className="text-xs text-gray-500">
                Will activate on next change
              </div>
            </div>
          </div>
        </div>

        {/* Philosophy */}
        <div className="mt-8 text-center text-gray-400 italic">
          "Every session builds on the last. Every bug teaches us something. Every success becomes a pattern."
        </div>
      </div>
    </div>
  );
}